import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, ScanBarcode, X } from "lucide-react";
import { formatMoney } from "@finora/config";
import { Skeleton, EmptyState } from "@finora/ui";
import { Package, Tag } from "lucide-react";
import { api, apiFetch } from "../../lib/api";
import type { ProductDto } from "@finora/types";

/** Lokalni tip - vidi napomenu u apps/api/src/modules/products/product-categories.service.ts
 * (DTO namerno nije dodat u deljeni @finora/types u ovom zadatku). */
interface ProductCategoryDto {
  id: string;
  companyId: string;
  name: string;
  colorHex: string | null;
  imageUrl: string | null;
  sortOrder: number;
}

export function ProductGrid({ onSelectProduct }: { onSelectProduct: (product: ProductDto) => void }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  // "Skeniraj barkod" (razgovor 28.09.2026) - otvara inline unos, Enter
  // traži artikal po barkodu (isti listProducts search koji već radi
  // barkod-substring pretragu na backendu, vidi ProductsService.list) i
  // dodaje ga u korpu ako je pronađen, ili prikazuje jasnu poruku ako nije.
  const [scanOpen, setScanOpen] = useState(false);
  const [scanValue, setScanValue] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);

  const { data: products, isLoading } = useQuery({
    queryKey: ["products", search],
    queryFn: () => api.listProducts({ search: search || undefined }),
  });

  // Realne kategorije (naziv + slika), ne više sirovi categoryId prikazan
  // kao tekst "Kategorija" - vidi product-categories.controller.ts (novi
  // endpoint, do sada nije postojao nijedan za upravljanje/čitanje
  // kategorija van sirovog Product.categoryId).
  const { data: categories } = useQuery({
    queryKey: ["product-categories"],
    queryFn: () => apiFetch<ProductCategoryDto[]>("/product-categories"),
  });

  const categoriesInUse = useMemo(() => {
    if (!products || !categories) return [];
    const idsInUse = new Set(products.map((p) => p.categoryId).filter(Boolean));
    return categories.filter((c) => idsInUse.has(c.id));
  }, [products, categories]);

  const filtered = useMemo(() => {
    if (!products) return [];
    if (!activeCategory) return products;
    return products.filter((p) => p.categoryId === activeCategory);
  }, [products, activeCategory]);

  async function handleScanSubmit() {
    const code = scanValue.trim();
    if (!code) return;
    setScanLoading(true);
    setScanError(null);
    try {
      const results = await api.listProducts({ search: code });
      const match = results.find((p) => p.barcode === code) ?? results[0];
      if (match) {
        onSelectProduct(match);
        setScanValue("");
        scanInputRef.current?.focus();
      } else {
        // WooCommerce konektor (HAFiskal dodatak): ako šifre nema u HAFiskalu, povuci artikal sa sajta.
        const imported = await api.importWooProduct(code).catch(() => null);
        if (imported?.product) {
          queryClient.invalidateQueries({ queryKey: ["products"] });
          onSelectProduct(imported.product);
          setScanValue("");
          scanInputRef.current?.focus();
        } else {
          setScanError(`Artikal sa šifrom "${code}" nije pronađen.`);
        }
      }
    } catch {
      setScanError("Pretraga po barkodu nije uspela. Pokušajte ponovo.");
    } finally {
      setScanLoading(false);
    }
  }

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex gap-2">
        <label className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pretraži artikal ili barkod"
            className="h-11 w-full rounded-lg border border-border bg-surface pl-9 pr-3 text-sm focus:border-brand focus:outline-none"
          />
        </label>
        <button
          type="button"
          aria-label="Skeniraj barkod"
          aria-pressed={scanOpen}
          onClick={() => {
            setScanOpen((v) => !v);
            setScanError(null);
            setTimeout(() => scanInputRef.current?.focus(), 0);
          }}
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border text-slate-500 hover:bg-slate-50 ${
            scanOpen ? "border-brand bg-brand-light text-brand" : "border-border bg-surface"
          }`}
        >
          <ScanBarcode className="h-5 w-5" aria-hidden />
        </button>
      </div>

      {scanOpen && (
        <div className="flex flex-col gap-1.5">
          <div className="flex gap-2">
            <input
              ref={scanInputRef}
              value={scanValue}
              onChange={(e) => {
                setScanValue(e.target.value);
                setScanError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleScanSubmit();
                }
              }}
              placeholder="Skenirajte ili unesite barkod i pritisnite Enter"
              className="h-11 flex-1 rounded-lg border border-brand bg-surface px-3 text-sm focus:outline-none"
            />
            <button
              type="button"
              aria-label="Zatvori unos barkoda"
              onClick={() => {
                setScanOpen(false);
                setScanValue("");
                setScanError(null);
              }}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-slate-400 hover:bg-slate-50"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          {scanLoading ? (
            <p className="text-xs text-slate-400">Tražim artikal...</p>
          ) : scanError ? (
            <p className="text-xs text-red-600">{scanError}</p>
          ) : null}
        </div>
      )}

      {categoriesInUse.length > 0 && (
        <div
          className="flex gap-2 overflow-x-auto pb-1"
          role="tablist"
          aria-label="Kategorije artikala"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeCategory === null}
            onClick={() => setActiveCategory(null)}
            className={`flex h-16 w-16 flex-shrink-0 flex-col items-center justify-center gap-1 rounded-xl border text-xs font-medium transition-colors ${
              activeCategory === null
                ? "border-brand bg-brand-light text-brand"
                : "border-border bg-surface text-slate-600 hover:border-brand"
            }`}
          >
            <Package className="h-5 w-5" aria-hidden />
            Sve
          </button>
          {categoriesInUse.map((category) => (
            <button
              key={category.id}
              type="button"
              role="tab"
              aria-selected={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
              title={category.name}
              className={`flex h-16 w-16 flex-shrink-0 flex-col items-center overflow-hidden rounded-xl border text-xs font-medium transition-colors ${
                activeCategory === category.id
                  ? "border-brand bg-brand-light text-brand"
                  : "border-border bg-surface text-slate-600 hover:border-brand"
              }`}
            >
              {category.imageUrl ? (
                <img src={category.imageUrl} alt="" className="h-9 w-full object-cover" />
              ) : (
                <span className="flex h-9 w-full items-center justify-center bg-app-bg">
                  <Tag className="h-4 w-4 text-slate-400" aria-hidden />
                </span>
              )}
              <span className="line-clamp-1 w-full px-1 pt-0.5 text-center leading-tight">
                {category.name}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* min-h-0 (24.09.2026, nastavak skrol ispravke) - ISTI flexbox
          problem kao main u AppShell.tsx, jedan nivo dublje: ovaj div je i
          sam flex-stavka (unutar "flex h-full flex-col" roditelja iznad),
          pa mu i on treba min-h-0 da overflow-auto stvarno ograniči
          sadržaj umesto da dozvoli listi proizvoda da se raširi na punu
          visinu i gurne celu stranicu u skrol. */}
      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Package} title="Nema artikala za prikaz" description="Promenite pretragu ili filter kategorije." />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => onSelectProduct(product)}
                className="flex min-h-[92px] items-stretch overflow-hidden rounded-xl border border-border bg-surface text-left shadow-subtle transition-colors hover:border-brand hover:bg-brand-light active:scale-[0.98]"
              >
                {/* Slika proizvoda (24.09.2026, korisnički zahtev: "SLIKA |
                    naziv", slika veća, levo, široka koliko i polje) - SAMO
                    vizuelni prikaz na kasi, ne dira se ništa u fiskalnom
                    dokumentu/računu - `product.imageUrl` je postojeće polje
                    (isto koje ProductsPage već prikazuje). Slika zauzima
                    ceo levi deo dugmeta (širina razmerna širini dugmeta,
                    puna visina), naziv i cena su desno. */}
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt=""
                    className="h-full w-2/5 flex-shrink-0 self-stretch object-cover"
                  />
                ) : (
                  <div className="h-full w-2/5 flex-shrink-0 self-stretch bg-app-bg" />
                )}
                <div className="flex min-w-0 flex-1 flex-col justify-between p-3">
                  <span className="line-clamp-2 text-sm font-medium text-slate-800">
                    {product.name}
                  </span>
                  <span className="text-sm font-semibold text-slate-900">
                    {formatMoney(product.unitPriceGross)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
